import { useEffect, useState } from "react";
import { Database, Download, HardDriveDownload, Info, Laptop, RotateCcw, ShieldCheck, Sparkles, Trash2, Zap } from "lucide-react";
import { clearAllLocalData, countLocalRecords } from "@/lib/session-store";
import { readStorageSnapshot, type StorageSnapshot } from "@/lib/memory-safety";
import { loadPrefs, savePrefs, type LocalPrefs } from "@/lib/prefs";

function formatBytes(bytes: number) {
  if (!bytes) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  const index = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  return `${(bytes / 1024 ** index).toFixed(index ? 1 : 0)} ${units[index]}`;
}

function Toggle({ enabled, onChange }: { enabled: boolean; onChange: () => void }) {
  return (
    <button className={enabled ? "toggle enabled" : "toggle"} onClick={onChange} aria-pressed={enabled}>
      <span />
    </button>
  );
}

export default function Settings() {
  const [prefs, setPrefs] = useState<LocalPrefs>({ rememberSession: true, reducedMotion: false });
  const [cleared, setCleared] = useState(false);
  const [storage, setStorage] = useState<StorageSnapshot>({ usage: 0, quota: 0, percent: 0 });
  const [counts, setCounts] = useState({ sessions: 0, signatures: 0 });

  const refresh = async () => {
    setStorage(await readStorageSnapshot());
    setCounts(await countLocalRecords().catch(() => ({ sessions: 0, signatures: 0 })));
  };

  useEffect(() => {
    setPrefs(loadPrefs());
    void refresh();
  }, []);

  const updatePref = (patch: Partial<LocalPrefs>) => {
    const next = { ...prefs, ...patch };
    setPrefs(next);
    savePrefs(next);
  };

  const clearWorkspace = async () => {
    await clearAllLocalData();
    setCleared(true);
    setPrefs({ rememberSession: true, reducedMotion: prefs.reducedMotion });
    await refresh();
  };

  return (
    <div className="settings-page">
      <div className="settings-intro">
        <div className="eyebrow accent-eyebrow">
          <span className="live-dot" /> Preferences
        </div>
        <h2>
          Quiet controls
          <br />
          <em>for a private tool.</em>
        </h2>
        <p>
          LocalPDF is deliberately small on the outside. PDFs never leave this browser. Adjust how this workspace
          behaves on this device.
        </p>
      </div>
      <div className="settings-grid">
        <section className="settings-card">
          <div className="settings-card-header">
            <div className="settings-icon coral-icon">
              <ShieldCheck size={18} />
            </div>
            <div>
              <h3>Privacy</h3>
              <p>Keep processing close to you.</p>
            </div>
          </div>
          <div className="setting-row">
            <div className="setting-copy">
              <strong>Remember this session</strong>
              <span>Store PDFs and layers in IndexedDB until you clear them.</span>
            </div>
            <Toggle enabled={prefs.rememberSession} onChange={() => updatePref({ rememberSession: !prefs.rememberSession })} />
          </div>
          <div className="setting-row destructive-row">
            <div className="setting-copy">
              <strong>Clear local workspace</strong>
              <span>
                {cleared
                  ? "IndexedDB, signatures, and preferences were wiped from this browser."
                  : "Permanently delete sessions, signatures, and local preferences."}
              </span>
            </div>
            <button className="clear-button" onClick={() => void clearWorkspace()}>
              <Trash2 size={14} /> {cleared ? "Cleared" : "Clear"}
            </button>
          </div>
        </section>
        <section className="settings-card">
          <div className="settings-card-header">
            <div className="settings-icon mint-icon">
              <Zap size={18} />
            </div>
            <div>
              <h3>Performance</h3>
              <p>Make larger files feel lighter.</p>
            </div>
          </div>
          <div className="setting-row">
            <div className="setting-copy">
              <strong>Worker processing</strong>
              <span>Heavy PDF work runs off the main thread.</span>
            </div>
            <div className="status-badge">
              <span className="status-dot" /> Ready
            </div>
          </div>
          <div className="setting-row">
            <div className="setting-copy">
              <strong>Reduced motion</strong>
              <span>Prefer less movement across the workspace.</span>
            </div>
            <Toggle enabled={prefs.reducedMotion} onChange={() => updatePref({ reducedMotion: !prefs.reducedMotion })} />
          </div>
          <div className="setting-row">
            <div className="setting-copy">
              <strong>Large PDF guardrails</strong>
              <span>Files over 250 MB are blocked. Very large files warn before processing.</span>
            </div>
            <div className="status-badge soft">On</div>
          </div>
        </section>
        <section className="settings-card">
          <div className="settings-card-header">
            <div className="settings-icon sky-icon">
              <Database size={18} />
            </div>
            <div>
              <h3>Storage</h3>
              <p>What this browser is actually keeping.</p>
            </div>
          </div>
          <div className="storage-meter">
            <div className="storage-meter-top">
              <span>Browser storage</span>
              <strong>
                {formatBytes(storage.usage)} <small>of {storage.quota ? formatBytes(storage.quota) : "unknown"}</small>
              </strong>
            </div>
            <div className="meter-track">
              <span style={{ width: `${Math.max(2, storage.percent)}%` }} />
            </div>
            <div className="storage-meter-bottom">
              <span>
                <HardDriveDownload size={13} /> IndexedDB
              </span>
              <span>
                {counts.sessions} sessions · {counts.signatures} signatures
              </span>
            </div>
          </div>
          <button className="storage-action" onClick={() => void refresh()}>
            <RotateCcw size={15} /> Recalculate storage
          </button>
        </section>
        <section className="settings-card about-card">
          <div className="settings-card-header">
            <div className="settings-icon lavender-icon">
              <Sparkles size={18} />
            </div>
            <div>
              <h3>About LocalPDF</h3>
              <p>A browser-first PDF workbench.</p>
            </div>
          </div>
          <div className="about-grid">
            <div>
              <span>Version</span>
              <strong>1.1.0</strong>
            </div>
            <div>
              <span>Engine</span>
              <strong>PDF.js + pdf-lib + qpdf WASM</strong>
            </div>
            <div>
              <span>Runtime</span>
              <strong>
                <Laptop size={13} /> Browser only
              </strong>
            </div>
            <div>
              <span>Network</span>
              <strong>No PDF uploads</strong>
            </div>
          </div>
          <div className="about-note">
            <Info size={15} />
            <span>
              PDFs never go to a server. OCR may load an English language model into this browser the first time you run
              it.
            </span>
          </div>
        </section>
      </div>
      <div className="settings-foot">
        <Download size={15} /> Preferences stay on this device only.
      </div>
    </div>
  );
}
